import { Subject } from 'rxjs';import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import {
  InspectionArticleDTO,
} from 'src/app/DTOs/ArticleTypeDTO';
import { CustomerDTO } from 'src/app/DTOs/CustomerDTO';
import { FactoryDTO } from 'src/app/DTOs/FactoryDTO';
import { InspectionDetailDTO } from 'src/app/DTOs/InspectionDetailDTO';
import { InspectionTypeDetailDTO } from 'src/app/DTOs/InspectionMasterData/InspectionType';
import { UserResponse } from 'src/app/Shared/Models/User.model';
import {
  inspectionStatusLabel,
  isInspectionStarted,
} from 'src/app/helper/InspectionStatus';
import { getUserDisplayName } from 'src/app/helper/user.utils';
import { InspectionArticleDialogComponent } from 'src/app/Components/inspection/inspection-article-dialog/inspection-article-dialog.component';
import { MasterDataService } from 'src/app/Services/master-data.service';
import { NotificationService } from 'src/app/Shared/prime-ng/notification.service';
import { PrimeNgModule } from 'src/app/Shared/prime-ng/prime-ng.module';

@Component({
  selector: 'app-inspection-detail',
  standalone: true,
  imports: [PrimeNgModule, FormsModule,
    CommonModule,
    RouterModule,
    InspectionArticleDialogComponent],
  templateUrl: './inspection-detail.component.html',
  styleUrl: './inspection-detail.component.css',
})
export class InspectionDetailComponent implements OnInit, OnDestroy {
  private readonly readonlyInspectionMessage =
    'Die Inspektion ist in execution, deshalb kann diese nicht mehr verändert werden.';
  private readonly defaultMeasurementCount = 3;
  private aqlPreviewRequestId = 0;

  inspection: InspectionDetailDTO = {
    inspectionId: 0,
    customerList: [],
    customer: null,
    customerContactPersonList: [],
    customerContactPerson: null,
    factoryList: [],
    factory: null,
    factoryContactPersonList: [],
    factoryContactPerson: null,
    reportNumber: '',
    orderNumber: '',
    inspectionTypeList: [],
    inspectionType: null,
    testingInstituteList: [],
    testingInstitute: null,
    sampleInstitute: null,
    inspectionDate: '',
    inspectionStatusId: 0,
    reportFinalizationDate: null,
    title: '',
    importeurList: null,
    importeur: null,
    inspectionArticles: [],
    countOfMeasurements: 0,
    quantity: null,
    aqlData: null,
    packingWidth: 0,
    packingHeight: 0,
    packingLength: 0,
    inspectionLevelList: [],
    inspectionLevel: null,
    createdBy: null,
    createdDate: null,
    startedBy: null,
    startedDate: null,
    inspectorList: [],
    primaryInspector: null,
    additionalInspectors: [],
    isRequirements: null,
    isPackings: null,
    isIdentialDesign: null,
    isDocument: null,
    isProductDetails: null,
    articleTypeList: [],
    imageUrls: [],
    articleSizeList: [],
  };

  inspectionDate: Date | null = null;
  reportFinalizationDate: Date | null = null;
  cancelDialog: boolean = false;
  articleEditorOpen: boolean = false;
  editingInspectionArticleId: number | null = null;
  showValidationErrors: boolean = false;
  private readonly _destroying$ = new Subject<void>();
  public loading: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private masterData: MasterDataService,
    private router: Router,
    private notificationService: NotificationService,
  ) { }

  ngOnInit(): void {
    this.loadInspectionDetail();
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  onCustomerChange(customer: CustomerDTO | null): void {
    if (this.inspection) {
      if (customer?.customerId) {
        this.inspection.customer =
          this.inspection.customerList.find(
            (c) => c.customerId === customer.customerId,
          ) || null;
      } else {
        this.inspection.customer = null;
      }
      this.inspection.customerContactPerson = null;
      this.inspection.customerContactPersonList = [];
      this.inspection.inspectionType = null;
      this.inspection.inspectionTypeList = [];

      if (customer?.customerId) {
        this.masterData
          .getContactPerson(customer.customerId, 'Customer')
          .subscribe({
            next: (data) => {
              if (this.inspection) {
                this.inspection.customerContactPersonList = data;
              }
            },
            error: (err) => {
              console.error('❌ Error loading contact persons:', err);
              this.notification('Error loading contact persons', 'error');
            },
          });

        this.loadInspectionTypes(customer.customerId);
      }
    }
  }

  onFactoryChange(factory: FactoryDTO | null): void {
    if (this.inspection) {
      if (factory?.factoryId) {
        this.inspection.factory =
          this.inspection.factoryList.find(
            (c) => c.factoryId === factory.factoryId,
          ) || null;
      } else {
        this.inspection.factory = null;
      }

      this.inspection.factoryContactPerson = null;
      this.inspection.factoryContactPersonList = [];

      if (factory?.factoryId) {
        this.masterData
          .getContactPerson(factory.factoryId, 'Factory')
          .subscribe({
            next: (data) => {
              if (this.inspection) {
                this.inspection.factoryContactPersonList = data;
              }
            },
            error: (err) => {
              console.error('❌ Error:', err);
              this.notification('Error loading contact persons', 'error');
            },
          });
      }
    }
  }

  isCustomerDisabled(): boolean {
    return !!this.inspection?.inspectionId && this.inspection.inspectionId > 0;
  }

  isReadonly(): boolean {
    return isInspectionStarted(this.inspection?.inspectionStatusId);
  }

  statusLabel(): string {
    const id = this.inspection?.inspectionStatusId;
    return id ? inspectionStatusLabel(id) : '';
  }

  startedByName(): string {
    return getUserDisplayName(this.inspection?.startedBy);
  }

  requiresMeasurementCount(): boolean {
    return this.inspection?.inspectionType?.isMeasurements === true;
  }

  hasValidMeasurementCount(): boolean {
    const count = this.inspection?.countOfMeasurements;
    return typeof count === 'number' && count > 0;
  }

  onInspectionTypeChange(inspectionType: InspectionTypeDetailDTO | null): void {
    if (!this.inspection) {
      return;
    }

    this.inspection.inspectionType = inspectionType?.auditTypeId
      ? this.inspection.inspectionTypeList.find(
          (type) => type.auditTypeId === inspectionType.auditTypeId,
        ) ?? inspectionType
      : null;

    this.applyMeasurementCountDefault();
  }

  onInspectionLevelChange(inspectionLevel: any): void {
    if (!this.inspection) {
      return;
    }

    this.inspection.inspectionLevel = inspectionLevel?.inspectionLevelId
      ? this.inspection.inspectionLevelList.find(
          (level) => level.inspectionLevelId === inspectionLevel.inspectionLevelId,
        ) ?? inspectionLevel
      : null;

    this.refreshAqlPreview();
  }

  onPrimaryInspectorChange(user: UserResponse | null): void {
    if (!this.inspection) {
      return;
    }

    const userId = this.getInspectorId(user);
    this.inspection.primaryInspector = userId
      ? this.resolveInspector(user)
      : null;

    this.inspection.additionalInspectors = (this.inspection.additionalInspectors ?? [])
      .filter((inspector) => this.getInspectorId(inspector) !== this.getInspectorId(this.inspection.primaryInspector));
  }

  onAdditionalInspectorsChange(users: UserResponse[] | null): void {
    if (!this.inspection) {
      return;
    }

    const selectedUsers = users ?? [];
    this.inspection.additionalInspectors = selectedUsers
      .map((user) => this.resolveInspector(user))
      .filter((user, index, list): user is UserResponse => {
        const userId = this.getInspectorId(user);
        return user !== null
          && userId !== null
          && userId !== this.getInspectorId(this.inspection.primaryInspector)
          && list.findIndex((candidate) => this.getInspectorId(candidate) === userId) === index;
      });
  }

  getInspectorDisplayName(user?: UserResponse | null): string {
    return getUserDisplayName(this.resolveInspector(user), 'Unknown Inspector');
  }

  getInspectorDisplayNames(users?: UserResponse[] | null): string {
    const names = (users ?? [])
      .map((user) => this.getInspectorDisplayName(user))
      .filter((name) => name && name !== 'Unknown Inspector');

    return names.length ? names.join(', ') : 'Select Additional Inspectors';
  }

  onQuantityChange(quantity: number | null): void {
    if (!this.inspection) {
      return;
    }

    this.inspection.quantity = quantity && quantity > 0 ? quantity : null;
    this.refreshAqlPreview();
  }

  public openCancelDialog() {
    this.cancelDialog = true;
  }

  public cancelHandler() {
    try {
      this.toggleCancelDialog();
      this.clearInspectionDraft();
      this.notification('Changes were canceled', 'info');
      this.navigateToOverview();
    } catch {
      this.notification('Error while canceling the changes', 'error');
    }
  }

  private validateInspection(): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!this.inspection) {
      errors.push('Inspection data is missing');
      return { valid: false, errors };
    }

    if (!this.inspection.title || this.inspection.title.trim() === '') {
      errors.push('Title is required');
    }

    if (
      !this.inspection.reportNumber ||
      this.inspection.reportNumber.trim() === ''
    ) {
      errors.push('Report Number is required');
    } else if (!/^\d+$/.test(this.inspection.reportNumber.trim())) {
      errors.push('Report Number must be numeric');
    }

    if (!this.inspection.customer?.customerId) {
      errors.push('Customer is required');
    }

    if (!this.inspection.factory?.factoryId) {
      errors.push('Factory is required');
    }

    if (!this.inspection.inspectionType?.auditTypeId) {
      errors.push('Inspection Type is required');
    }

    if (!this.inspection.inspectionDate) {
      errors.push('Inspection Date is required');
    }

    if (this.requiresMeasurementCount() && !this.hasValidMeasurementCount()) {
      errors.push(
        'Count of Measurements is required when the inspection type includes Measurements',
      );
    }

    if (this.inspection.inspectionLevel?.inspectionLevelId && !this.inspection.quantity) {
      errors.push('Quantity is required when Inspection Level is selected');
    }

    if (this.inspection.quantity && !this.inspection.inspectionLevel?.inspectionLevelId) {
      errors.push('Inspection Level is required when Quantity is set');
    }

    return {
      valid: errors.length === 0,
      errors: errors,
    };
  }

  saveInspection() {
    if (!this.inspection) return;

    if (this.isReadonly()) {
      this.notification(this.readonlyInspectionMessage, 'error');
      return;
    }

    this.inspection.reportNumber = this.inspection.reportNumber?.trim() ?? '';

    if (this.inspectionDate) {
      this.inspection.inspectionDate = this.formatDate(this.inspectionDate);
    } else {
      this.inspection.inspectionDate = null;
    }
    if (this.reportFinalizationDate) {
      this.inspection.reportFinalizationDate = this.formatDate(
        this.reportFinalizationDate,
      );
    } else {
      this.inspection.reportFinalizationDate = null;
    }

    this.showValidationErrors = true;
    const validation = this.validateInspection();

    if (!validation.valid) {
      console.error('❌ Validation failed:', validation.errors);
      const errorMessage = validation.errors.join('\n• ');
      this.notification(
        `Please fill in all required fields:\n\n• ${errorMessage}`,
        'error',
      );
      return;
    }

    const isUpdate =
      this.inspection.inspectionId && this.inspection.inspectionId > 0;
    if (isUpdate) {
      this.masterData.updateInspection(this.inspection).subscribe({
        next: () => {
          this.clearInspectionDraft();
          this.notification('Inspection successfully updated', 'success');
          this.navigateToOverview();
        },
        error: (err) => {
          console.error('❌ Update Error:', err);
          const errorMsg =
            err?.error?.message || err?.message || 'Unknown error';
          this.notification(`Error updating inspection: ${errorMsg}`, 'error');
        },
      });
    } else {
      this.masterData.createInspection(this.inspection).subscribe({
        next: () => {
          this.clearInspectionDraft();
          this.notification('Inspection successfully created', 'success');
          this.navigateToOverview();
        },
        error: (err) => {
          const errorMsg =
            err?.error?.message || err?.message || 'Unknown error';
          this.notification(
            `Error creating inspection: ${errorMsg}`,
            'error',
          );
        },
      });
    }
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  cancelEdit() {
    this.clearInspectionDraft();
    this.navigateToOverview();
  }
  private loadInspectionDetail() {
    this.loading = true;
    const idParam = this.route.snapshot.paramMap.get('id');
    const id = idParam ? Number(idParam) : null;
    const shouldApplyPlannerPrefill = this.shouldApplyPlannerPrefill(id);

    if (shouldApplyPlannerPrefill) {
      this.clearInspectionDraft();
    }

    this.masterData.getInspectionDetail(id ?? undefined).subscribe({
      next: (data: InspectionDetailDTO) => {
        this.inspection = shouldApplyPlannerPrefill
          ? data
          : (this.getStoredInspectionDraft() ?? data);
        this.inspectionDate = this.inspection.inspectionDate
          ? new Date(this.inspection.inspectionDate)
          : null;
        this.reportFinalizationDate = this.inspection.reportFinalizationDate
          ? new Date(this.inspection.reportFinalizationDate)
          : null;
        if (this.inspection.customer?.customerId) {
          this.inspection.customer =
            this.inspection.customerList.find(
              (c) => c.customerId === this.inspection!.customer!.customerId,
            ) || null;
        }

        if (this.inspection.customerContactPerson?.databaseId) {
          this.inspection.customerContactPerson =
            this.inspection.customerContactPersonList.find(
              (p) =>
                p.databaseId ===
                this.inspection!.customerContactPerson!.databaseId,
            ) || null;
        }

        if (this.inspection.factory?.factoryId) {
          this.inspection.factory =
            this.inspection.factoryList.find(
              (f) => f.factoryId === this.inspection!.factory!.factoryId,
            ) || null;
        }

        if (this.inspection.factoryContactPerson?.databaseId) {
          this.inspection.factoryContactPerson =
            this.inspection.factoryContactPersonList.find(
              (p) =>
                p.databaseId ===
                this.inspection!.factoryContactPerson!.databaseId,
            ) || null;
        }

        if (this.inspection.inspectionLevel?.inspectionLevelId) {
          this.inspection.inspectionLevel =
            this.inspection.inspectionLevelList.find(
              (level) =>
                level.inspectionLevelId ===
                this.inspection!.inspectionLevel!.inspectionLevelId,
            ) ?? this.inspection.inspectionLevel;
        }

        if (this.getInspectorId(this.inspection.primaryInspector)) {
          this.inspection.primaryInspector =
            this.resolveInspector(this.inspection.primaryInspector);
        }

        this.inspection.additionalInspectors = (this.inspection.additionalInspectors ?? [])
          .map((inspector) => this.resolveInspector(inspector))
          .filter((inspector, index, list): inspector is UserResponse => {
            const inspectorId = this.getInspectorId(inspector);
            return inspector !== null
              && inspectorId !== null
              && inspectorId !== this.getInspectorId(this.inspection!.primaryInspector)
              && list.findIndex((candidate) => this.getInspectorId(candidate) === inspectorId) === index;
          });

        if (this.inspection.testingInstitute?.laboratoryId) {
          this.inspection.testingInstitute =
            this.inspection.testingInstituteList.find(
              (laboratory) =>
                laboratory.laboratoryId ===
                this.inspection!.testingInstitute!.laboratoryId,
            ) || this.inspection.testingInstitute;
        }

        if (this.inspection.sampleInstitute?.laboratoryId) {
          this.inspection.sampleInstitute =
            this.inspection.testingInstituteList.find(
              (laboratory) =>
                laboratory.laboratoryId ===
                this.inspection!.sampleInstitute!.laboratoryId,
            ) || this.inspection.sampleInstitute;
        }

        if (this.inspection.customer?.customerId) {
          this.loadInspectionTypes(
            this.inspection.customer.customerId,
            this.inspection.inspectionType?.auditTypeId,
          );
        } else {
          this.applyMeasurementCountDefault();
        }

        if (shouldApplyPlannerPrefill) {
          this.applyPlannerPrefill();
        }

        this.refreshAqlPreview(false);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.notification('Error while loading Inspection Detail', 'error');
      },
    });
  }

  toggleCancelDialog() {
    this.cancelDialog = !this.cancelDialog;
  }

  notification(message: string, state: any) {
    this.notificationService.show({
      content: message,
      animation: { type: 'fade', duration: 400 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: state, icon: true },
    });
  }

  onImageSelected(event: any): void {
    const file: File = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.inspection!.imageUrls = [e.target.result];
      };
      reader.readAsDataURL(file);
    }
  }

  public addInspectionArticle(): void {
    if (this.isReadonly()) {
      this.notification(this.readonlyInspectionMessage, 'error');
      return;
    }

    if (!this.inspection) {
      console.log('inspection is null');
      return;
    }

    this.persistInspectionDraft();
    this.editingInspectionArticleId = null;
    this.articleEditorOpen = true;
  }

  public editInspectionArticle(article: InspectionArticleDTO): void {
    if (this.isReadonly()) {
      this.notification(this.readonlyInspectionMessage, 'error');
      return;
    }

    this.persistInspectionDraft();
    this.editingInspectionArticleId = article.inspectionArticleId;
    this.articleEditorOpen = true;
  }

  public deleteInspectionArticle(index: number): void {
    if (this.isReadonly()) {
      this.notification(this.readonlyInspectionMessage, 'error');
      return;
    }

    if (!this.inspection?.inspectionArticles) {
      return;
    }

    if (index < 0 || index >= this.inspection.inspectionArticles.length) {
      return;
    }

    const deletedArticleId = this.inspection.inspectionArticles[index]?.inspectionArticleId;
    this.inspection.inspectionArticles.splice(index, 1);

    if (this.editingInspectionArticleId === deletedArticleId) {
      this.closeInspectionArticleEditor();
    }

    this.persistInspectionDraft();
  }

  public onInspectionArticleSaved(): void {
    const draft = this.getStoredInspectionDraft();

    if (draft) {
      this.inspection.inspectionArticles = draft.inspectionArticles ?? [];
    }

    this.closeInspectionArticleEditor();
  }

  public onInspectionArticleEditorCanceled(): void {
    this.closeInspectionArticleEditor();
  }

  public getInspectionArticleSizesText(article: InspectionArticleDTO): string {
    if (!article.sizes || article.sizes.length === 0) {
      return '-';
    }

    return article.sizes
      .slice()
      .sort((a, b) => a.sorting - b.sorting)
      .map((size) => size.sizeTitle)
      .join(', ');
  }

  private persistInspectionDraft(): void {
    if (typeof window === 'undefined' || !this.inspection) {
      return;
    }

    const draft: InspectionDetailDTO = {
      ...this.inspection,
      inspectionDate: this.inspectionDate
        ? this.formatDate(this.inspectionDate)
        : null,
      reportFinalizationDate: this.reportFinalizationDate
        ? this.formatDate(this.reportFinalizationDate)
        : null,
    };

    window.sessionStorage.setItem(
      this.getInspectionDraftStorageKey(),
      JSON.stringify(draft),
    );
  }

  private getStoredInspectionDraft(): InspectionDetailDTO | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const rawDraft = window.sessionStorage.getItem(
      this.getInspectionDraftStorageKey(),
    );

    if (!rawDraft) {
      return null;
    }

    try {
      return JSON.parse(rawDraft) as InspectionDetailDTO;
    } catch {
      return null;
    }
  }

  private clearInspectionDraft(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.sessionStorage.removeItem(this.getInspectionDraftStorageKey());
  }

  private getInspectionDraftStorageKey(): string {
    const inspectionId = this.route.snapshot.paramMap.get('id') ?? '0';
    return `inspection-detail-draft:${inspectionId}`;
  }

  private shouldApplyPlannerPrefill(inspectionId: number | null): boolean {
    return inspectionId === 0 && this.route.snapshot.queryParamMap.get('plannerCreate') === '1';
  }

  private applyPlannerPrefill(): void {
    const queryParams = this.route.snapshot.queryParamMap;
    const prefillInspectionDate = queryParams.get('prefillInspectionDate');
    const prefillPrimaryInspectorId = Number(queryParams.get('prefillPrimaryInspectorId'));

    if (prefillInspectionDate && /^\d{4}-\d{2}-\d{2}$/.test(prefillInspectionDate)) {
      this.inspection.inspectionDate = prefillInspectionDate;
      this.inspectionDate = new Date(`${prefillInspectionDate}T00:00:00`);
    }

    if (Number.isInteger(prefillPrimaryInspectorId) && prefillPrimaryInspectorId > 0) {
      const primaryInspector = this.inspection.inspectorList.find(
        (inspector) => this.getInspectorId(inspector) === prefillPrimaryInspectorId,
      );

      if (primaryInspector) {
        this.inspection.primaryInspector = primaryInspector;
        this.inspection.additionalInspectors = [];
      }
    }
  }

  private refreshAqlPreview(fetchFromServer: boolean = true): void {
    if (!this.inspection) {
      return;
    }

    const inspectionLevelId = this.inspection.inspectionLevel?.inspectionLevelId;
    const quantity = this.inspection.quantity;

    if (!inspectionLevelId || !quantity || quantity <= 0) {
      this.inspection.aqlData = null;
      return;
    }

    if (!fetchFromServer && this.inspection.aqlData?.quantity === quantity) {
      return;
    }

    const requestId = ++this.aqlPreviewRequestId;
    this.masterData.getInspectionAqlPreview(inspectionLevelId, quantity).subscribe({
      next: (aqlData) => {
        if (!this.inspection || requestId !== this.aqlPreviewRequestId) {
          return;
        }

        this.inspection.aqlData = aqlData;
      },
      error: () => {
        if (!this.inspection || requestId !== this.aqlPreviewRequestId) {
          return;
        }

        this.inspection.aqlData = null;
      },
    });
  }

  private loadInspectionTypes(
    customerId: number,
    selectedAuditTypeId?: number | null,
  ): void {
    this.masterData.getInspectionTypeOverview().subscribe({
      next: (response) => {
        if (!this.inspection) {
          return;
        }

        const filteredTypes = response.inspectionTypes.filter(
          (type) => type.customer?.customerId === customerId,
        );

        this.inspection.inspectionTypeList = filteredTypes;

        if (selectedAuditTypeId) {
          this.inspection.inspectionType =
            filteredTypes.find(
              (type) => type.auditTypeId === selectedAuditTypeId,
            ) ?? this.inspection.inspectionType;
        }

        this.applyMeasurementCountDefault();
      },
      error: (err) => {
        console.error('❌ Error loading inspection types:', err);
        this.notification('Error loading inspection types', 'error');
      },
    });
  }

  private applyMeasurementCountDefault(): void {
    if (!this.inspection || !this.requiresMeasurementCount()) {
      return;
    }

    if (!this.hasValidMeasurementCount()) {
      this.inspection.countOfMeasurements = this.defaultMeasurementCount;
    }
  }

  private closeInspectionArticleEditor(): void {
    this.articleEditorOpen = false;
    this.editingInspectionArticleId = null;
  }

  private navigateToOverview(): void {
    this.router.navigate(['/inspection-overview'], {
      queryParams: this.getOverviewQueryParams(),
    });
  }

  private getOverviewQueryParams(): Record<string, string> {
    const queryParams = this.route.snapshot.queryParamMap;
    const entries = ['search', 'status', 'skip', 'take']
      .map(key => {
        const value = queryParams.get(key);
        return value ? [key, value] as const : null;
      })
      .filter((entry): entry is readonly [string, string] => entry !== null);

    return Object.fromEntries(entries);
  }

  private resolveInspector(user?: UserResponse | null): UserResponse | null {
    const userId = this.getInspectorId(user);
    if (!userId || !user) {
      return user ?? null;
    }

    return this.inspection?.inspectorList.find(
      (inspector) => this.getInspectorId(inspector) === userId,
    ) ?? {
      ...user,
      id: user.id ?? userId,
    };
  }

  private getInspectorId(user?: UserResponse | null): number | null {
    return user?.id ?? user?.userId ?? null;
  }
}
